import { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";

// css
import styles from "./addForm.module.scss";
import { CommonSelect, RequiredFieldAstrick } from "../../../../../components/Common/Common";
import { GENDER_OPTIONS, GRADE_OPTIONS } from "config/constants";
import ReactSelectItem from "components/shared/ReactSelectItemComponent";
import { generatePath, useNavigate, useParams, Link } from "react-router-dom";
import { useCreateSchoolClientMutation, useGetSchoolClientDataQuery, useUpdateSchoolClientMutation } from "store/services/clients/school.client";
import useAutoUpdateInitialValues from "hooks/useAutoUpdateInitialValues";
import { FieldArray, Formik } from "formik";
import schoolClientFormSchema from "./SchoolClientForm.schema";
import { SCHOOL_CLIENT_DETAIL, SCHOOL_CLIENT_LIST } from "config/paths";
import { MIME_TYPES } from "config/constants";
import AppInputField from "components/shared/FormComponents/AppInputField";
import AppFileUploadField from "components/shared/FormComponents/AppFileUploadField";
import LoadingButton from "components/shared/LoadingButton";
import { useSchoolDivisonLookupQuery } from "store/services/schoolsDivisons";
import { MinusIcon, PlusIcon } from "Assets/icons";
import { useClientsServiceStatusListQuery } from "store/services/utility";
import { cloneDeep } from "lodash";
import { successMessageToast } from "helpers/common";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import AppDatePicker from "components/shared/FormComponents/AppDatePicker";
import { useCreateNoteMutation, useGetNoteQuery } from "store/services/note";
import ConfirmationPop from "components/Modals/ConfirmationPop";
import LoadingOverlay from "components/shared/LoadingOverlay";
import NotesComponent from "components/shared/FormComponents/NotesComponent";
import { useArchiveGuardianMutation } from "store/services/archive";
import GuardianPicker from "components/shared/Cllients/GuardianPicker";
import DuplicateClientModal from "components/Modals/DuplicateClientModal";
import { useCheckDuplicateClientMutation } from "store/services/client";
import moment from "moment";

const AddClientSchool = () => {
  const { id: clientId } = useParams();
  const editMode = !!clientId;
  const navigate = useNavigate();

  const {
    data: clientData,
    isLoading,
    isError: getClientError,
    refetch
  } = useGetSchoolClientDataQuery(clientId, { skip: !clientId });
  const [createClient, { isLoading: createClientLoading }] = useCreateSchoolClientMutation();
  const [updateClient] = useUpdateSchoolClientMutation();
  const [checkDuplicateClient, { isLoading: duplicateCheckLoading }] = useCheckDuplicateClientMutation();

  // Remove Guardian
  const [archiveGuardian, { isLoading: archiveGuardianLoading }] = useArchiveGuardianMutation();

  const [createNote] = useCreateNoteMutation();

  const { data: schoolDivisionsList, isLoading: schoolDivisionsListLoading } = useSchoolDivisonLookupQuery();
  const { data: noteDetails, isLoading: getNoteLoading } = useGetNoteQuery({ clientId, moduleName: "clients-school" }, { skip: !clientId });
  const teachers = clientData?.client_detail?.client_guardian_mapping?.map((guardian) => guardian?.client_guardian?.client_role?.slug == "teacher" ? guardian?.client_guardian?.id : null)?.filter(Boolean);

  //  Service status lookup
  const { data: serviceStatusList, isLoading: serviceStatusListLoading } = useClientsServiceStatusListQuery({ module: "client_service", client_type: "school" });
  const serviceStatusOptions = serviceStatusList?.map(i => ({ value: i.id, label: i.title }));

  // Use custom hook with dependencies (e.g., designationData)
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [pendingClientPayload, setPendingClientPayload] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const [initialValues, setInitialValues] = useAutoUpdateInitialValues(
    defaultInitialValues,
    [clientData]
  );

  useEffect(() => {
    if (!clientData) {
      return;
    }
    let teachers = [];
    // let parents = [];
    clientData?.client_detail?.client_guardian_mapping?.length &&
      clientData?.client_detail?.client_guardian_mapping?.forEach((guardian) => {
        let { id, firstname, lastname, phone, email, client_role } = guardian?.client_guardian || {};
        let guardiandetails = {
          id,
          firstname,
          lastname: {
            value: id,
            label: lastname,
          },
          phone,
          email
        };
        if (client_role?.slug == "teacher") {
          teachers.push(guardiandetails);
        }
        // else if (client_role?.slug == "parent") {
        //   parents.push(guardiandetails);
        // }
      })

    setInitialValues({
      ...initialValues,
      firstname: clientData?.firstname,
      lastname: clientData?.lastname,
      dob: clientData?.client_detail?.dob,
      gender: GENDER_OPTIONS.find(item => item.value === clientData?.client_detail?.gender),
      grade: GRADE_OPTIONS.find(item => item.value === clientData?.client_detail?.grade),
      city_town: clientData?.client_detail?.city_town,
      school_division: {
        value: clientData?.client_detail?.client_school_detail?.school_division,
        label: clientData?.client_detail?.client_school_detail?.client_school_division?.title,
      },
      date_of_expiry: moment(clientData?.client_detail?.date_of_expiry).format("YYYY-MM-DD"),
      school_profile: {
        value: clientData?.client_detail?.client_school_detail?.school_profile,
        label: clientData?.client_detail?.client_school_detail?.client_school?.name,
      },
      teachers: teachers?.length ? teachers : [{
        ...singleTeacher
      }],
      // parents: parents,
      documents:
        clientData?.clientResources?.map(resource => resource.type == "attachment" && ({
          id: resource.id,
          attachment_key: resource.attachment_key,
          attachment_name: resource.attachment_name
        }))?.filter(Boolean) || [],
      // service_status: serviceStatusOptions?.find(x => x.value == clientData?.client_detail?.service_status) || "",
      status: clientData?.status,
    })
  }, [clientData, serviceStatusOptions]);

  const handleClickCancel = () => {
    navigate(SCHOOL_CLIENT_LIST);
  };

  const createOrUpdateClient = async (payload) => {
    try {
      let result;
      let values = cloneDeep(payload);

      delete values?.note;

      // format values
      values.gender = payload?.gender?.value;
      values.grade = payload?.grade?.value;
      values.school_division = payload?.school_division?.value;
      values.school_profile = payload?.school_profile?.value;
      values.date_of_expiry = moment(payload?.date_of_expiry).format("YYYY-MM-DD")
      // values.service_status = payload?.service_status?.value;

      values.teachers = values.teachers?.map(row => ({
        ...row,
        lastname: row.lastname.label
      }));

      if (editMode) {
        result = await updateClient({
          id: clientId,
          payload: values,
        });
      } else {
        result = await createClient(values);
      }

      if (result?.error) {
        console.error("Error creating School Client", result.error);
      } else {

        if (payload?.note) await createNote({ module: "clients-school", client_id: result?.data?.data?.id, note: payload?.note })
        successMessageToast(result?.data?.message);
        navigate(SCHOOL_CLIENT_LIST);
      }
    } catch (err) {
      console.error("Error creating School Client", err);
    }
  };

  const handleSubmit = async (payload) => {
    if (editMode) {
      await createOrUpdateClient(payload);
      return;
    }

    try {
      const duplicateResult = await checkDuplicateClient({
        clientType: "school",
        data: {
          firstname: payload?.firstname?.trim(),
          lastname: payload?.lastname?.trim(),
          dob: moment(payload?.dob).format("YYYY-MM-DD"),
        },
      }).unwrap();
      const matches = duplicateResult?.data?.matches || [];

      if (matches.length) {
        setDuplicateMatches(matches);
        setPendingClientPayload(cloneDeep(payload));
        setShowDuplicateModal(true);
        return;
      }

      await createOrUpdateClient(payload);
    } catch (err) {
      console.error("Error checking for duplicate School Client", err);
    }
  };

  const handleCreateAnyway = async () => {
    if (!pendingClientPayload) return;

    setShowDuplicateModal(false);
    await createOrUpdateClient(pendingClientPayload);
    setPendingClientPayload(null);
  };

  const handleViewDuplicateClient = (id) => {
    setShowDuplicateModal(false);
    setPendingClientPayload(null);
    navigate(generatePath(SCHOOL_CLIENT_DETAIL, { id }));
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false);
    setPendingClientPayload(null);
  };

  const handleGuardianArchive = async () => {

    try {
      setShowModal(false);

      const list = cloneDeep(currentItem?.list || []);
      const index = list.findIndex(x => x.id == currentItem?.id);
      if (index > -1) {

        delete list[index];
        typeof currentItem?.setFieldValue == "function" && currentItem?.setFieldValue("teachers", list?.filter(Boolean));

        const enterToDelete = clientData?.client_detail?.client_guardian_mapping?.find(x => x?.client_guardian?.id == currentItem?.id);
        const result = await archiveGuardian(enterToDelete?.id);

        if (result?.error) return;
        successMessageToast(result?.data?.message);
      }

    } catch (err) {
      console.error("Error archive guardian", err);
    }
  };

  const handleAttachDocument = (name, value, prevValues, setFieldValue) => {
    try {
      let cloneValues = cloneDeep(prevValues);
      cloneValues.push({
        attachment_key: value,
        attachment_name: value,
      })
      setFieldValue(name, cloneValues);
    } catch (err) {
      console.error("Failed to attach document", err);
    }
  };

  return (
    <>
      <ConfirmationPop
        confirmation={showModal}
        setConfirmation={setShowModal}
        confirmHandler={handleGuardianArchive}
        type="delete"
      />
      <DuplicateClientModal
        show={showDuplicateModal}
        matches={duplicateMatches}
        onCancel={handleCancelDuplicate}
        onCreateAnyway={handleCreateAnyway}
        onViewClient={handleViewDuplicateClient}
        isCreating={createClientLoading}
      />
      <section className={`${styles.addForm} position-relative py-4 addForm`}>
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="pageHead pb-3 d-flex align-items-center">
                <Link
                  className="themeBlue fw-sbold back-to-btn"
                  to="/client/school/"
                >
                  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                  </svg> Back
                </Link>
                <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit Client" : "Add New Client"}</h6>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              {isLoading || getNoteLoading || serviceStatusListLoading || schoolDivisionsListLoading ? (
                <LoadingIndicator />
              ) : getClientError ? (
                <ErrorComponent />
              ) : (
                <Formik
                  initialValues={initialValues}
                  validationSchema={schoolClientFormSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    handleSubmit,
                    isSubmitting,
                    values,
                    errors,
                    setFieldValue,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <div
                        className="py-3"
                        style={{ borderTop: "1px solid #41a2a84d" }}
                      >
                        {archiveGuardianLoading && <LoadingOverlay />}

                        <Row>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Last Name
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="lastname"
                              placeholder="e.g Smith"
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              First Name
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="firstname"
                              placeholder="e.g John"
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              DOB
                              <RequiredFieldAstrick />
                            </label>
                            <AppDatePicker
                              name="dob"
                              maxDate={new Date()}
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>

                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Gender Assigned at Birth
                              <RequiredFieldAstrick />
                            </label>
                            <CommonSelect
                              name="gender"
                              placeholder={"Select Gender"}
                              option={GENDER_OPTIONS}
                              cstmOption={ReactSelectItem}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Grade
                              <RequiredFieldAstrick />
                            </label>
                            <CommonSelect
                              name="grade"
                              placeholder={"Please Select Grade"}
                              option={GRADE_OPTIONS}
                            // cstmOption={CustomGenderOption}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              City/Town
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="city_town"
                              placeholder="City/Town"
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              School Division
                              <RequiredFieldAstrick />
                            </label>
                            <CommonSelect
                              name="school_division"
                              placeholder={"Please Select Division"}
                              option={
                                schoolDivisionsList?.length && schoolDivisionsList?.map(item => ({
                                  value: item.id,
                                  label: item.title,
                                }))
                              }
                              onChange={() => {
                                setFieldValue("school_profile", null); 
                                setFieldValue('teachers', [{ ...singleTeacher }])
                              }}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              School Name
                              <RequiredFieldAstrick />
                            </label>
                            <CommonSelect
                              name="school_profile"
                              placeholder={"Please Select School"}
                              option={function () {
                                let schoolsList =
                                  values.school_division?.value &&
                                  schoolDivisionsList?.length > 0 &&
                                  schoolDivisionsList.find(division => division.id === values.school_division?.value)?.schools;
                                if (!schoolsList?.length) {
                                  return;
                                }
                                return cloneDeep(schoolsList || [])?.sort((a, b) => a?.name?.localeCompare(b?.name))?.map(item => ({
                                  value: item.id,
                                  label: item.name,
                                }))
                              }()}
                              onChange={(e) => {
                                setFieldValue('school_profile', e);
                                setFieldValue('teachers', [{ ...singleTeacher }])
                              }}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Date of Expiry
                              <RequiredFieldAstrick />
                            </label>
                            <AppDatePicker
                              name="date_of_expiry"
                              minDate={new Date()}
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0"
                            >
                              Status
                            </label>
                            <AppSelectField
                              name={"status"}
                              placeholder={"Please Select"}
                              className={`${styles.formControl} form-control`}
                              options={[
                                {
                                  value: "active",
                                  label: "Active",
                                },
                                {
                                  value: "inactive",
                                  label: "Inactive",
                                },
                                {
                                  value: "archived",
                                  label: "Archived",
                                },
                                {
                                  value: "discharged",
                                  label: "Discharged",
                                }
                              ]}
                            />
                          </Col>
                          {/* <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Service Status
                              <RequiredFieldAstrick />
                            </label>
                            <CommonSelect
                              name="service_status"
                              placeholder={"Please Select Service Status"}
                              option={serviceStatusOptions}
                            />
                          </Col> */}
                        </Row>
                        <Row>
                          <Col md="12" sm="12" className="my-2">
                            {/* <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 d-flex"
                            >
                              Notes
                            </label> */}
                            {
                              editMode ?
                                <NotesComponent
                                  notes={noteDetails}
                                  className={`${styles.formControl} form-control`}
                                  module={"clients-school"}
                                  client_id={clientId}
                                /> :
                                <AppInputField
                                  name={`note`}
                                  placeholder="Notes"
                                  fieldType={"textarea"}
                                  maxLength={500}
                                  className={`${styles.formControl} form-control`}
                                />
                            }
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="12" sm="12">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0 mt-2"
                            >
                              Teacher's Details
                            </label>

                            <Col md="12" sm="12" className="my-0">
                              <FieldArray name="teachers">
                                {({ insert, remove, push }) => (
                                  <ul className="list-unstyled ps-0 mb-0">
                                    {values.teachers.length > 0 &&
                                      values.teachers.map((area, index) => {
                                        let isLastItem = index === values.teachers.length - 1;
                                        return (
                                          <Row className="align-items-center" key={index}>
                                            <Col lg="10">
                                              <Row>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    Last Name
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <GuardianPicker
                                                    isSchoolIdRequired={true}
                                                    schoolId={values?.school_profile?.value}
                                                    index={index}
                                                    type="teachers"
                                                    name={`teachers.${index}.lastname`}
                                                    placeholder={"Search Last Name"}
                                                  />
                                                </Col>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    First Name
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <AppInputField
                                                    disabled={!!area?.id}
                                                    name={`teachers.${index}.firstname`}
                                                    placeholder="e.g John Smith"
                                                    className={`${styles.formControl} form-control`}
                                                  />
                                                </Col>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    Email
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <AppInputField
                                                    disabled={!!area?.id}
                                                    name={`teachers.${index}.email`}
                                                    placeholder="John@gmail.com"
                                                    className={`${styles.formControl} form-control`}
                                                  />
                                                </Col>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    Phone
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <AppInputField
                                                    disabled={!!area?.id}
                                                    name={`teachers.${index}.phone`}
                                                    placeholder="+1234567890"
                                                    className={`${styles.formControl} form-control`}
                                                    maxLength={25}
                                                  />
                                                </Col>
                                              </Row>
                                            </Col>
                                            <Col lg="2">
                                              <Button
                                                className="border-0 p-0"
                                                variant="transparnet"
                                                disabled={archiveGuardianLoading}
                                              >
                                                {isLastItem ? (

                                                  values.teachers.length > 1 ? (
                                                    <>
                                                      <PlusIcon onClick={() => push({ ...singleTeacher })} />
                                                      <MinusIcon onClick={() => {
                                                        if (area.id && teachers.includes(area.id)) {
                                                          setCurrentItem({ ...area, setFieldValue, list: values?.teachers });
                                                          setShowModal(true);
                                                          return;
                                                        }
                                                        else remove(index)
                                                      }} />
                                                    </>
                                                  ) : <PlusIcon onClick={() => push({ ...singleTeacher })} />

                                                ) : <MinusIcon onClick={() => {

                                                  if (area.id && teachers.includes(area.id)) {
                                                    setCurrentItem({ ...area, setFieldValue, list: values?.teachers });
                                                    setShowModal(true);
                                                    return;
                                                  }
                                                  else remove(index)
                                                }} />
                                                }
                                              </Button>
                                            </Col>
                                          </Row>
                                        )
                                      })}
                                  </ul>
                                )}
                              </FieldArray>
                            </Col>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="4" sm="12">
                            <Col md="12" sm="12" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0"
                              >
                                Attach Documents
                              </label>
                              <AppFileUploadField
                                name="documents"
                                acl={"private"}
                                accept="image/*, application/pdf, .doc, .docx"
                                allowedTypes={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.jpg, MIME_TYPES.webp, MIME_TYPES.doc, MIME_TYPES.docx]}
                                className={`${styles.formControl} form-control`}
                                onChange={(name, value) => {
                                  handleAttachDocument(name, value, values?.documents, setFieldValue);
                                }}
                              />
                            </Col>
                          </Col>
                        </Row>

                      </div>
                      <Row className="mt-4">
                        <Col className="my-2" lg="12">
                          <div
                            className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                            style={{ borderTop: "1px solid #41a2a84d" }}
                          >
                            <LoadingButton
                              type="submit"
                              className="d-flex align-items-center justify-content-center commonBtn"
                              isLoading={isSubmitting || duplicateCheckLoading}
                            >
                              Save
                            </LoadingButton>
                            <Button
                              className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                              onClick={handleClickCancel}
                            >
                              Cancel
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

const singleTeacher = {
  firstname: "",
  lastname: "",
  phone: "",
  email: ""
}

const singleParent = {
  firstname: "",
  lastname: "",
  phone: "",
  email: ""
}

const defaultInitialValues = {
  firstname: "",
  lastname: "",
  dob: "",
  gender: "",
  grade: "",
  city_town: "",
  school_division: "",
  school_profile: "",
  date_of_expiry: "",
  teachers: [{
    ...singleTeacher
  }],
  // parents: [{
  //   ...singleParent
  // }],
  documents: [],
  // service_status: "",
  status: "active",
  note: ""
}

export default AddClientSchool;
